// src/manager/ManagerDashboard.tsx — FINAL FIXED & COMPLETE VERSION
// All errors resolved: Table type import + Loader2 import + 50-150% RTP

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import ConfirmModal from '../shared/components/ConfirmModal';
import { useModal } from './utils/modal';
import { useManager, type Table } from './hooks/useManager'; // ← FIXED: Table type import
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react'; // ← FIXED: Loader2 import

// All existing pages
import CreateTable from './pages/CreateTable';
import RegisterUser from './pages/RegisterUser';
import SetCashoutRate from './pages/SetCashoutRate';
import Cashouts from './pages/Cashouts';
import ManageUsers from './pages/ManageUsers';
import DistributeCredits from './pages/DistributeCredits';
import ChangePassword from './pages/ChangePassword';
import GlobalRtpControl from './pages/GlobalRtpControlFixed';
import VolatilityControl from './pages/VolatilityControl';
import TimeRTPSchedule from './pages/TimeRTPSchedule';
import Account from './pages/Account';
import RiskControl from './pages/RiskControl';

const RTP_MIN_PERCENT = 50;
const RTP_MAX_PERCENT = 150;

// Inline RTP + Tables component
function MyTablesAndRtp() {
  const { tables, fetchTables, setTableRtp } = useManager();
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [rtpValue, setRtpValue] = useState(95);
  const [payoutValue, setPayoutValue] = useState(1);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const handleEdit = (table: Table) => {
    setSelectedTable(table);
    setRtpValue(table.rtp * 100);
    setPayoutValue(table.payout_ratio);
  };

  const handleSaveRtp = async () => {
    if (!selectedTable) return;

    if (rtpValue < RTP_MIN_PERCENT || rtpValue > RTP_MAX_PERCENT) {
      toast.error(`RTP must be between ${RTP_MIN_PERCENT}% and ${RTP_MAX_PERCENT}% for managers`);
      return;
    }

    setEditLoading(true);
    try {
      await setTableRtp({
        table_id: selectedTable.id,
        rtp: rtpValue,
        payout_ratio: payoutValue,
      });
      toast.success('RTP & Payout updated!');
      setSelectedTable(null);
      fetchTables();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update RTP');
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* My Tables List + Inline RTP Edit */}
      <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-800">My Tables</h3>
        </div>

        {tables.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            No tables yet. Create one above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <tr>
                  <th className="p-4 text-left">Name</th>
                  <th className="p-4 text-left">RTP</th>
                  <th className="p-4 text-left">Payout Ratio</th>
                  <th className="p-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tables.map(table => (
                  <tr key={table.id} className="border-t hover:bg-gray-50">
                    <td className="p-4 font-medium">{table.name}</td>
                    <td className="p-4 font-bold text-center" style={{
                        color: table.rtp >= 0.96 ? '#10b981' : table.rtp >= 0.93 ? '#f59e0b' : '#ef4444'
                      }}>
                      {(table.rtp * 100).toFixed(2)}%
                    </td>
                    <td className="p-4 text-center">{Number(table.payout_ratio).toFixed(2)}</td>
                    <td className="p-4">
                      <button
                        onClick={() => handleEdit(table)}
                        className="bf-glossy-btn px-3 py-1 text-white rounded text-sm"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Inline RTP Edit Form */}
        {selectedTable && (
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <h4 className="text-lg font-medium mb-4">Edit "{selectedTable.name}"</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1">RTP (%)</label>
                <input
                  type="range"
                  min={RTP_MIN_PERCENT}
                  max={RTP_MAX_PERCENT}
                  step="0.1"
                  value={rtpValue}
                  onChange={e => setRtpValue(Number(e.target.value))}
                  className="w-full accent-purple-600 h-2"
                />
                <div className="text-center font-bold text-purple-700 mt-2">
                  {rtpValue.toFixed(1)}%
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Payout Ratio</label>
                <input
                  type="number"
                  step="0.01"
                  value={payoutValue}
                  onChange={e => setPayoutValue(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                />
              </div>

              <div className="md:col-span-2 flex justify-end gap-4">
                <button
                  onClick={() => setSelectedTable(null)}
                  className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRtp}
                  disabled={editLoading}
                  className={`bf-glossy-btn px-6 py-2 text-white rounded flex items-center ${
                    editLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {editLoading ? (
                    <Loader2 className="animate-spin mr-2" size={18} />
                  ) : null}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ManagerDashboard() {
  const [activeSection, setActiveSection] = useState('manage-tables'); // Default to tables + RTP
  const { validateToken, error, setError, socket, fetchTables, fetchCashouts, fetchUsers, fetchAccount, account, disconnectManagerSocket, clearManagerSession } = useManager();
  const { isOpen, message, onConfirm, showModal, closeModal } = useModal();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const section = location.pathname.replace(/^\/manager\/?/, '') || 'manage-tables';
    const aliases: Record<string, string> = {
      'manage-my-tables': 'manage-tables',
      'manage-my-users': 'manage-users',
      'register-player': 'register-user',
      'cashout-requests': 'cashouts',
    };
    setActiveSection(aliases[section] ?? section);
  }, [location.pathname]);

  useEffect(() => {
    validateToken().catch(() => {});
  }, [validateToken]);

  useEffect(() => {
    fetchAccount().catch(() => {});
  }, [fetchAccount]);

  // Real-time listeners
  useEffect(() => {
    if (!socket) return;

    socket.on('rtp-updated', (data) => {
      toast(`RTP updated: ${(data.rtp * 100).toFixed(2)}%`);
      if (['manage-tables'].includes(activeSection)) fetchTables();
    });

    socket.on('cashout-updated', () => {
      toast('Cashout request updated');
      if (activeSection === 'cashouts') fetchCashouts();
    });

    socket.on('credits-updated', () => {
      toast('Credits updated for users');
      if (activeSection === 'manage-users') fetchUsers();
    });

    return () => {
      socket.off('rtp-updated');
      socket.off('cashout-updated');
      socket.off('credits-updated');
    };
  }, [socket, activeSection, fetchTables, fetchCashouts, fetchUsers]);

  const handleLogout = () => {
    showModal('Are you sure you want to logout?', () => {
      disconnectManagerSocket();
      clearManagerSession();
      setError('');
      navigate('/manager/login', { replace: true });
      closeModal();
    });
  };

  const sections: { [key: string]: React.ReactNode } = {
    'manage-tables': <MyTablesAndRtp />,          // Inline RTP + Tables + Create
    'create-table': <CreateTable />,
    'register-user': <RegisterUser />,
    'distribute-credits': <DistributeCredits />,
    'set-cashout-rate': <SetCashoutRate />,
    cashouts: <Cashouts />,
    'manage-users': <ManageUsers />,
    'global-rtp': <GlobalRtpControl />,
    'risk-control': <RiskControl />,
    'volatility-control': <VolatilityControl />,
    'time-rtp-schedule': <TimeRTPSchedule />,
    'change-password': <ChangePassword />,
    account: <Account />,
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar
        activeSection={activeSection}
        onSectionChange={(id) => {
          setActiveSection(id);
          navigate(id === 'manage-tables' ? '/manager' : `/manager/${id}`);
        }}
        onLogout={handleLogout}
      />

      <div className="flex-1 p-6 overflow-auto">
        {account && (
          <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Manager Panel</h1>
              <p className="text-sm text-gray-600">{account.name} | {account.login_id}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 xl:min-w-[720px]">
            <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
              <div className="text-sm text-purple-700">Manager Balance</div>
              <div className="text-2xl font-bold text-purple-900">{Number(account.credits || 0).toFixed(0)}</div>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <div className="text-sm text-emerald-700">Received / Available</div>
              <div className="text-2xl font-bold text-emerald-900">{Number(account.credits || 0).toFixed(0)}</div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-sm text-amber-700">Distributed</div>
              <div className="text-2xl font-bold text-amber-900">{Number(account.distributed_total || 0).toFixed(0)}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm text-slate-700">Deducted Back</div>
              <div className="text-2xl font-bold text-slate-900">{Number(account.deducted_total || 0).toFixed(0)}</div>
            </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded flex justify-between items-center shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-700 hover:text-red-900 font-bold">✕</button>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg min-h-[calc(100vh-2rem)] overflow-hidden">
          {sections[activeSection] || (
            <div className="p-8 text-center text-gray-600 text-xl">
              Select a section from the sidebar
            </div>
          )}
        </div>

        <ConfirmModal
          isOpen={isOpen}
          message={message}
          onConfirm={() => onConfirm && onConfirm()}
          onCancel={closeModal}
        />
      </div>
    </div>
  );
}
